import { useMemo, useState } from "react"
import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import { toast } from "@/hooks/use-toast"
import type { FieldConfig } from "@/components/form-builder"
import { useUploadDoc } from "@/services/queries/usePerson"
import { DOCUMENT_TYPES } from "../constants/document-types"
import { personAttachmentsSchema } from "../schemas/person-attachments-schema"
import type { PersonAttachmentsFormData } from "../types/person-attachments"
import { getFilePreview, saveFilePreview } from "../utils/upload-helpers"
import { useUpdateOnboardingSteps } from "@/services/queries/useUsers"
import { useAuthContext } from "@/context/authentication/auth-context"

interface UsePersonAttachmentsProps {
  personId: number
  onSuccess: () => void
}

export function usePersonAttachments({ personId, onSuccess }: UsePersonAttachmentsProps) {
  const form = useForm<PersonAttachmentsFormData>({
    resolver: zodResolver(personAttachmentsSchema),
    defaultValues: {},
  })

  const { mutateAsync: uploadDoc, isPending } = useUploadDoc(false)

  const [previewMap, setPreviewMap] = useState<Record<string, string | null>>(() => {
    const initial: Record<string, string | null> = {}
    DOCUMENT_TYPES.forEach((doc) => {
      initial[doc.field] = getFilePreview(personId, doc.field)
    })
    return initial
  })

  const handleFileUpload = async (file: File | null, field: string) => {
    if (!file) return

    const docConfig = DOCUMENT_TYPES.find((doc) => doc.field === field)
    if (!docConfig) return

    try {
      const formData = new FormData()
      formData.append("file", file)
      formData.append("type", docConfig.apiType)

      const response = await uploadDoc({ data: formData })
      const previewUrl =
        (response as { data?: { url?: string } })?.data?.url ||
        URL.createObjectURL(file)

      saveFilePreview(personId, field, previewUrl)
      setPreviewMap((prev) => ({ ...prev, [field]: previewUrl }))

      
    } catch (error) {
      console.error("Erro ao fazer upload do documento:", error)
      toast({
        title: "Erro ao enviar documento",
        description: "Não foi possível enviar o documento.",
        variant: "destructive",
      })
    }
  }

  const attachmentFields = useMemo<FieldConfig[]>(() => {
    return DOCUMENT_TYPES.map((doc) => ({
      name: doc.field,
      type: "file-preview",
      label: doc.label,
      required: doc.required,
      colSpan: doc.colSpan,
      fileConfig: {
        accept: doc.accept,
        maxSize: doc.maxSize,
        showPreview: true,
        previewUrl: previewMap[doc.field],
      },
      onFileSelect: (file) => handleFileUpload(file, doc.field),
    }))
  }, [previewMap])

  const onFormSubmit = async () => {
    toast({
      title: "Anexos enviados",
      description: "Todos os documentos foram processados.",
      variant: "success",
    })
    onSuccess()
  }

  return {
    form,
    isPending,
    onFormSubmit,
    attachmentFields,
  }
}
